// filterLabelUtils.ts

export const EMBED_LABEL_MAX_LENGTH = 80;

/** Truncate user-facing filter/option labels for the embed UI. */
export function truncateLabel(
  label: string | undefined | null,
  max = EMBED_LABEL_MAX_LENGTH
): string {
  if (!label) return "";
  if (label.length <= max) return label;
  return `${label.slice(0, Math.max(0, max - 1))}…`;
}

type FilterOption = {
  label?: string;
  value?: string | number | string[];
  frequency?: number;
  min?: number;
  max?: number;
  key?: string;
};

type FilterConfig = {
  name?: string;
  label: string;
  component?: string;
  body_key?: string;
  options: FilterOption[];
};

type FiltersApiResponse = Record<string, FilterConfig>;

type LabelMap = Record<string | number, string>;
type LabelMaps = Record<string, LabelMap>;

/**
 * Builds a label map from a filter's options.
 */
export const buildLabelMap = (options: FilterOption[]): LabelMap =>
  options.reduce((acc, option) => {
    if (option.value !== undefined && !Array.isArray(option.value)) {
      acc[option.value] = option.label ?? String(option.value);
    }
    return acc;
  }, {} as LabelMap);

/**
 * Builds label maps for all filters returned by the API.
 */
export const buildAllLabelMaps = (filters: FiltersApiResponse): LabelMaps =>
  Object.fromEntries(
    Object.values(filters).map((filter) => [
      filter.name,
      buildLabelMap(filter.options),
    ])
  );

/**
 * Transforms an array of values to a comma-separated label string using the provided map.
 */
export const getLabel = (
  values: (string | number)[] | undefined,
  labelMap: LabelMap,
  fallback = ""
): string =>
  (Array.isArray(values) ? values : [])
    .map((v) => labelMap[v] || v)
    .join(", ") || fallback;

/**
 * Wrapper that uses a full labelMaps object to get labels by filter name.
 */
export const getLabelFromFilter = (
  filterName: string,
  values: (string | number)[] | undefined,
  labelMaps: LabelMaps,
  fallback = ""
): string => getLabel(values, labelMaps[filterName] || {}, fallback);

// filterLabelUtils.ts

export const capitalise = (str: string): string =>
  str.charAt(0).toUpperCase() + str.slice(1);

/**
 * Returns the display label for a filter.
 * If values exist → resolves to label(s) from labelMap.
 * If empty or undefined → capitalises the filter name.
 */

export function getCostLabel(
  value: number | undefined,
  min: number,
  max: number,
  isMobile: boolean = false
): string {
  //   if (value === undefined || value === null) return "";

  // Mobile-specific logic: simplified formatting
  if (isMobile) {
    return `< $${formatCost(value ?? 0)}`;
  }

  // Default (desktop) logic
  return `Up to $${formatCost(value || max)}`;
}

function formatCost(amount: number): string {
  return amount.toLocaleString(); // e.g. 15000 → "15,000"
}

export function getFilterDisplayLabel(
  filterName: string,
  selectedValues: (string | number)[] | string | number | undefined,
  options: { label: string; value: string | number }[],
  opts?: { forceCountStyle?: boolean; displayLabel?: string }
): string {
  // Normalize to array
  let valuesArray: (string | number)[];
  if (Array.isArray(selectedValues)) {
    valuesArray = selectedValues;
  } else if (selectedValues != null) {
    valuesArray = [selectedValues];
  } else {
    valuesArray = [];
  }

  const baseLabel = truncateLabel(
    opts?.displayLabel || getDefaultLabel(filterName)
  );

  if (valuesArray.length === 0) return baseLabel;

  // Static label + badge for large option sets, or tag groups (Highlights, Sunny, …)
  if (opts?.forceCountStyle || options.length > 4) {
    return baseLabel;
  }

  // Dynamic label for small sets (Availability, Bedrooms, …). Join all selected
  // labels; the pill has a fixed width and ellipsis-truncates the overflow.
  const selectedLabels = options
    .filter((opt) => valuesArray.includes(opt.value))
    .map((opt) => truncateLabel(opt.label))
    .join(", ");

  return selectedLabels || baseLabel;
}

// Explicit mapping for specific filter names → display labels
const FILTER_NAME_LABELS: Record<string, string> = {
  date_availability: "Availability",
  base_price: "Price",
  qty_bedrooms: "Bedrooms",
};

function getDefaultLabel(name: string): string {
  if (!name) return "";
  const override = FILTER_NAME_LABELS[name];
  if (override) return override;
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/**
 * Returns badge count string when the filter uses count-style pills
 * (large option sets, or tag groups like Highlights / Sunny).
 */
export const getFilterBadge = (
  filterName: string,
  selectedValues: (string | number)[] | undefined,
  filters: FiltersApiResponse,
  opts?: { forceCountStyle?: boolean }
): string => {
  const filter = filters[filterName];
  const count = Array.isArray(selectedValues) ? selectedValues.length : 0;
  const useCount = opts?.forceCountStyle || (filter?.options?.length ?? 0) > 4;

  return useCount && count > 0 ? String(count) : "";
};
